import Image from 'next/image';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { DoctorIcon } from '../../montefiore/icons/doctor';
import { MEAC_ORG_KEY } from './header-data';

export const HeaderMainMenu: React.FC = () => {
  const { t } = useTranslation(MEAC_ORG_KEY);
  const analytics = useAnalytics();
  const { config } = useOrgContext();
  const findDoctorUrl = `${config.companyUrl}/providers`;

  const trackLink = useCallback(
    (clickText: string) => {
      analytics.navigationClickEvent({
        navigationType: 'top-bar',
        clickText,
        linkType: 'link',
      });
    },
    [analytics],
  );

  return (
    <div className={'relative border-b border-gray-200 bg-white'}>
      <div className={'mf-wrap'}>
        <div
          className={
            'lg:landscape:min-h-[5.125rem] xl:min-h-[5.125rem] flex min-h-[3.625rem] items-center justify-between py-4'
          }
        >
          {/* Hamburger Menu */}
          <div
            className={
              'lg:landscape:-left-24 xl:-left-24 relative flex items-center'
            }
          >
            <a
              href={config.companyUrl}
              className={
                'mf-button mf-button--site-montefiore mf-button--light mf-button--base mf-button--rounded mf-top-bar__button mf-top-bar__button--icon mf-top-bar__button--burger inline-block align-bottom'
              }
              aria-label={t('aria.openNavigationMenu')}
              onClick={() => {
                trackLink(t('montefiore.menu'));
              }}
            >
              <span className={'mf-button__ripple'}>
                <span
                  className={'mf-button__ripple__path'}
                  style={{ left: '0px', top: '0px' }}
                />
              </span>
              <span className={'mf-button__icon mf-button__icon--leading'}>
                <span className={'mf-icon mf-icon--small mf-icon--inherit'}>
                  <span />
                  <span />
                  <span />
                </span>
              </span>
              <span className={'mf-button__text'}>{t('montefiore.menu')}</span>
            </a>
          </div>

          {/* Logo */}
          <div className={'mf-top-bar__logo'}>
            <a
              href={config.companyUrl}
              onClick={() => {
                trackLink('Logo');
              }}
            >
              <Image
                src={config.branding.logoUrl}
                alt={t('aria.logoAlt', {
                  orgName: 'Montefiore Einstein Advanced Care',
                })}
                className={'mf-logo block w-[12.5rem]'}
                width={200}
                height={40}
                priority={true}
              />
            </a>
          </div>

          <div className={'flex items-center'}>
            {/* Search */}
            <a
              href={`${config.companyUrl}/search`}
              className={
                'mf-button mf-button--site-montefiore mf-button--light mf-button--base mf-button--rounded mf-top-bar__button mf-top-bar__button--icon mf-top-bar__button--search inline-block align-bottom'
              }
              onClick={() => {
                trackLink(t('montefiore.search'));
              }}
            >
              <span className={'mf-button__ripple'}>
                <span
                  className={'mf-button__ripple__path'}
                  style={{ left: '0px', top: '0px' }}
                />
              </span>
              <span className={'mf-button__icon mf-button__icon--leading'}>
                <span className={'mf-icon mf-icon--small mf-icon--inherit'}>
                  <span />
                  <span />
                  <span />
                </span>
              </span>
              <span className={'mf-button__text'}>
                {t('montefiore.search')}
              </span>
            </a>

            {/* Find a Doctor */}
            <ul className={'lg:landscape:block xl:block ml-4 hidden list-none'}>
              <li className={'inline-block'}>
                <a
                  className={
                    'mf-button mf-button--site-montefiore mf-button--ghost-dark mf-button--base mf-button--rounded mf-top-bar__button'
                  }
                  href={findDoctorUrl}
                  onClick={() => {
                    trackLink(t('montefiore.findADoctor'));
                  }}
                >
                  <span className={'mf-button__ripple'}>
                    <span
                      className={'mf-button__ripple__path'}
                      style={{ left: '0px', top: '0px' }}
                    />
                  </span>
                  <span className={'mf-button__icon mf-button__icon--leading'}>
                    <span className={'mf-icon mf-icon--large mf-icon--inherit'}>
                      <DoctorIcon
                        className={'mf-icon__svg'}
                        aria-hidden={'true'}
                        focusable={'false'}
                      />
                    </span>
                  </span>
                  <span className={'mf-button__text'}>
                    {t('montefiore.findADoctor')}
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
